import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Loader2, CheckCircle2, Download } from "lucide-react";
import { analytics } from "../../lib/analytics/ga4";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailSignup({
  source = "site",
  magnet = "Free Canadian Budget Planner",
  title = "Get the free Canadian Budget Planner",
  description = "Join our newsletter and get a free Google Sheets budget planner delivered to your inbox.",
  compact = false,
  freeFile = null,
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, { email, name: name || null, source, magnet, consent: true });
      analytics.emailSignup(source);
      analytics.freeDownloadClick(magnet);
      setStatus("done");
      toast.success("You're in! Check your inbox for the free planner.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <div data-testid="email-signup-success" className="rounded-xl border border-brand/20 bg-brand/5 p-5">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-brand" />
          <p className="text-sm font-medium text-brand">
            {freeFile ? `You're in! Download your free file below — we also sent a copy to ${email}.` : `Success! Your free planner is on its way to ${email}.`}
          </p>
        </div>
        {freeFile && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/${freeFile}`}
            data-testid="free-file-download"
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-action px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-action-hover"
          >
            <Download className="h-4 w-4" /> Download your free spreadsheet
          </a>
        )}
      </div>
    );
  }

  return (
    <div data-testid="email-signup" className={`rounded-2xl border border-brand/15 bg-brand/5 ${compact ? "p-5" : "p-7"}`}>
      <div className="mb-4 flex items-center gap-2">
        <Gift className="h-5 w-5 text-action" />
        <h3 className="font-heading text-lg font-bold text-brand">{title}</h3>
      </div>
      {!compact && <p className="mb-4 text-sm text-muted-foreground">{description}</p>}
      <form onSubmit={submit} className="space-y-3">
        {!compact && (
          <input
            data-testid="signup-name" type="text" placeholder="First name (optional)"
            value={name} onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-border bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/30"
          />
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            data-testid="signup-email" type="email" required placeholder="you@email.com"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full flex-1 rounded-lg border border-border bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/30"
          />
          <button
            data-testid="signup-submit" type="submit" disabled={status === "loading"}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-action px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-action-hover disabled:opacity-60"
          >
            {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get it free"}
          </button>
        </div>
      </form>
      <p className="mt-3 text-xs text-muted-foreground">No spam. Unsubscribe anytime. We store only your email — never your calculations.</p>
    </div>
  );
}
